import React, { useState, useEffect, useRef } from "react";
import Navbar from "../../Components/Common/Navbar";
import { getImagePath } from "../../Utils/Utilities";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../../Components/ui/Button";
import { useBrand } from "../../Utils/BrandContext";
import apiService from "../../Utils/APIService";
import { toast } from "sonner";
import { useIsMobile } from "../../Hooks/useIsMobile";

// ─── Feature Data ──────────────────────────────────────────────────────────────

const featuresWithIcons = [
  { name: "CRM", icon: "fa fa-users" },
  { name: "Quotes", icon: "fas fa-file-alt" },
  { name: "Orders", icon: "fas fa-box" },
  { name: "Billing", icon: "fas fa-money-bill-wave" },
  { name: "E-Way Bill", icon: "fas fa-truck" },
  { name: "E-Invoice", icon: "fas fa-file-invoice" },
  { name: "Accounts", icon: "fas fa-inr" },
  { name: "Recovery", icon: "fas fa-hand-holding-usd" },
  { name: "Inventory", icon: "fas fa-warehouse" },
  { name: "Projects", icon: "fas fa-project-diagram" },
  { name: "Manufacturing", icon: "fas fa-industry" },
];

// ─── Main Component ────────────────────────────────────────────────────────────

const Restart = () => {
  const { brand } = useBrand();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const isMobile = useIsMobile();
  const brandName = brand === "bz" ? "Biziverse" : "QikERP";
  const logoSrc =
    brand === "bz"
      ? getImagePath("/Images/Biziverse.png")
      : getImagePath("/Images/QikERP.png");

  const PIXEL_ID = "1652655355901268";
  const EVENT_NAME = "PageView";

  // --- META PIXEL INTEGRATION START ---
  useEffect(() => {
    if (window.fbq) {
      window.fbq("track", EVENT_NAME);
      return;
    }

    // Standard Meta Boilerplate (Never changes)
    const fbq = function () {
      fbq.callMethod
        ? fbq.callMethod.apply(fbq, arguments)
        : fbq.queue.push(arguments);
    };
    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    // Use the variables here
    window.fbq("init", PIXEL_ID);
    window.fbq("track", EVENT_NAME);
  }, []);

  const sendSensor = async (actionID, actionTxt, otp = "0") => {
    try {
      await apiService.sendCallSensorReq(actionID, actionTxt, {
        leadPIN: otp,
        mobile: mobile || searchParams.get("i") || "0",
        device: window.innerWidth < 768 ? "M" : "D",
      });
    } catch (err) {
      console.error("Sensor log failed:", err);
    }
  };

  useEffect(() => {
    const i = searchParams.get("i");
    if (i) setMobile(i);
    setTimeout(() => inputRef.current?.focus(), 300);

    const srcTag = searchParams.get("src") || "RestartPage";
    sendSensor(998, `${i || "New"}: Link Req Start - ${srcTag}`);
  }, []);

  const handleKnowMore = () => {
    if (brand === "qikerp") {
      window.open("https://www.biziverse.com/web/whatsnew?m=qikerp", "_blank");
    } else {
      window.open("https://www.biziverse.com/web/whatsnew", "_blank");
    }
  };

  const handleRestart = async () => {
    if (!mobile || mobile.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }
    setError("");
    setLoading(true);

    const srcTag = searchParams.get("src") || "RestartPage";
    sendSensor(998, `${mobile}: Link Req Click - ${srcTag}`);

    try {
      const bzParamsJson = JSON.stringify([{ o: searchParams.get("o") || "" }]);
      const resp = await apiService.sendOTP(
        mobile,
        "",
        "lead~JoinRestart",
        bzParamsJson,
      );
      const data = resp?.d;

      if (data?.error) {
        toast.error(data.message || "Error validating number.");
        setLoading(false);
        return;
      }

      const validateUser = data?.data ? JSON.parse(data.data) : null;
      if (validateUser && validateUser.Table && validateUser.Table.length > 0) {
        const table = validateUser.Table[0];
        if (table.C === 1) {
          window.location.href = `https://biziverse.com/Index.html?i=${mobile}`;
          return;
        }
        if (table.L === 1) {
          const leadOTP = table.LeadOTP || "";
          sendSensor(1, "Link request", leadOTP);
          navigate(`/QikERP/Guide?i=${mobile}&o=${leadOTP}&z=main&m=${brand}`);
          return;
        }
        window.location.href = `https://biziverse.com/Join01.html?i=${mobile}&x=Desktop_QikERP&m=${brand}`;
      } else {
        window.location.href = `https://biziverse.com/Join01.html?i=${mobile}&x=Desktop_QikERP&m=${brand}`;
      }
    } catch (error) {
      toast.error("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden">
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src={`https://www.facebook.com/tr?id=${PIXEL_ID}&ev=${EVENT_NAME}&noscript=1`}
        />
      </noscript>

      <Navbar
        logo={logoSrc}
        showBuyNow={false}
        isButtonPrior={false}
        onLoginClick={handleRestart}
      />

      <div className="flex-1 flex items-center justify-center p-5 lg:p-10">
        <div className="max-w-8xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center justify-center">
          {/* ── LEFT SIDE: Full Desktop Image ── */}
          <div className="flex w-auto flex-col h-full justify-center items-end order-2 lg:order-1">
            <div className="flex sm:max-w-2xl md:max-w-3xl flex-col h-full justify-center items-start gap-10">
              {/* Image Wrapper */}
              <div className="relative hidden md:flex w-full">
                {/* Desktop Dashboard */}
                <img
                  src={getImagePath("/Images/Features/Dashboard.png")}
                  alt="Desktop Dashboard"
                  className="w-full h-auto object-cover rounded-[10px] shadow-2xl ring-[7px] ring-border"
                />

                {/* Mobile Dashboard (Overlay) */}
                <img
                  src={getImagePath("/Images/Features/DashboardMobile.png")}
                  alt="Mobile Dashboard"
                  className="absolute max-w-36 bottom-[-50px] right-[-30px] w-[30%] md:w-[25%] rounded-[12px] shadow-2xl ring-[5px] ring-border bg-white"
                />
              </div>
              <div className="flex md:hidden relative w-full">
                {/* Desktop Dashboard */}
                <img
                  src={getImagePath("/Images/Features/Dashboard.png")}
                  alt="Desktop Dashboard"
                  className="w-full mb-14 h-auto object-cover rounded-[5px] shadow-2xl ring-[5px] ring-border"
                />

                {/* Mobile Dashboard (Overlay) */}
                <img
                  src={getImagePath("/Images/Features/DashboardMobile.png")}
                  alt="Mobile Dashboard"
                  className="absolute max-w-20 bottom-[0px] right-[20px] w-[30%] md:w-[25%] rounded-[5px] shadow-2xl ring-[5px] ring-border bg-white"
                />
              </div>

              <div className="flex md:w-auto w-full max-w-full md:max-w-2xl">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleKnowMore}
                  className="border md:w-auto w-full bg-gray-50 hover:bg-primary border-border text-sm"
                >
                  <i className="fas fa-external-link-alt mr-2 text-[13px]" />
                  Explore all new updates
                </Button>
              </div>
            </div>
          </div>

          {/* ── RIGHT SIDE: CTA & Content ── */}
          <div className="flex max-w-3xl flex-col justify-center order-1 lg:order-2 lg:pl-8 pt-6 md:pt-0">
            {/* Header Area */}
            <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-4">
              <span className="text-3xl lg:text-4xl font-bold text-primary">
                Welcome to
              </span>
              <img
                src={logoSrc}
                alt="Brand Logo"
                className={`${brand === "bz" ? "h-10 md:h-16" : "h-12 md:h-16"} object-contain`}
              />
            </div>

            <h1
              className="text-3xl lg:text-4xl font-bold text-text-primary mb-6 md:mb-8"
              style={{ lineHeight: "1.2" }}
            >
              Easy & Powerful Tool to <br className="md:flex hidden"></br>
              Accelerate Business
            </h1>

            {/* Feature Icons Grid */}
            <div className="mb-6 md:mb-8 flex flex-wrap gap-3">
              {featuresWithIcons.map((feature, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 border border-border rounded-[4px] px-2 md:px-3 py-2 bg-white shadow-sm hover:border-primary transition-colors"
                >
                  <i
                    className={`${feature.icon} flex-shrink-0 text-primary`}
                    style={{ fontSize: isMobile ? "14px" : "16px" }}
                  ></i>
                  <span className="text-xs md:text-sm text-text-primary font-bold whitespace-nowrap">
                    {feature.name}
                  </span>
                </div>
              ))}
            </div>

            <p className="text-lg text-text-secondary leading-relaxed font-semibold mb-6">
              Trusted by <span className="text-primary">45,000+</span>{" "}
              Businesses across India
            </p>

            <div className="w-fit min-w-full lg:min-w-0 flex flex-col">
              {/* Offer Box */}
              <div
                className={`flex items-center gap-3 md:gap-4 rounded-[5px] border border-primary/20 ${brand === "bz" ? "bg-orange-50/50" : "bg-green-50/50"} p-4 md:p-5 mb-6 md:mb-10`}
              >
                <div className="md:flex hidden w-10 h-10 md:w-12 md:h-12 rounded-[5px] bg-primary items-center justify-center flex-shrink-0 shadow-lg">
                  <i className="fas fa-star text-white text-base md:text-lg" />
                </div>
                <div>
                  <p className="text-sm md:text-lg font-bold text-text-primary1">
                    Restart New Subscription @ Huge Discount
                  </p>
                  <p className="text-[14px] md:text-sm text-text-secondary leading-relaxed">
                    {brand === "qikerp"
                      ? "On the first year of QikERP, the latest edition of Biziverse ERP & CRM"
                      : "Upgrade and restart Biziverse at a huge discount"}
                  </p>
                </div>
              </div>

              {/* Input Block */}
              <div className=" md:px-0">
                <div className="text-md md:hidden flex font-semibold text-text-secondary mb-2">
                  Enter Mobile to Get Started
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 h-12 sm:max-w-80 border border-gray-300 rounded focus-within:border-primary bg-background-light transition-colors">
                    <input
                      ref={inputRef}
                      type="tel"
                      maxLength={10}
                      value={mobile}
                      onChange={(e) => {
                        setMobile(e.target.value.replace(/\D/g, ""));
                        if (error) setError("");
                      }}
                      onKeyDown={(e) => e.key === "Enter" && handleRestart()}
                      placeholder="Enter Mobile No."
                      className={`h-11 w-full sm:w-72 px-4 rounded border-2 border-solid transition-all outline-none border-gray-400 bg-background-light focus:border-primary`}
                      id="mobile"
                    />
                  </div>

                  <Button
                    onClick={handleRestart}
                    size="lg"
                    variant="primary"
                    className="w-full whitespace-nowrap sm:w-auto px-8 h-12 text-md shadow-lg shadow-primary/20 md:mt-0 mt-2"
                    disabled={loading}
                  >
                    {loading ? "Validating..." : "Restart Now"}
                  </Button>
                </div>

                {error && (
                  <p className="text-red-600 text-[11px] font-medium flex items-center mt-2">
                    <i className="fas fa-exclamation-circle mr-1.5" />
                    {error}
                  </p>
                )}
              </div>
            </div>

            {/* Trust Footer Row */}
            <div className="md:flex hidden flex-wrap items-center gap-x-6 md:gap-x-10 gap-y-3 mt-8 md:mt-10 pt-6 md:pt-8 border-t border-gray-100">
              {[
                { icon: "fas fa-bolt", label: "Quick Implementation" },
                { icon: "fas fa-headset", label: "Quick Support" },
                { icon: "fas fa-rocket", label: "Quick Transformation" },
              ].map((t, i) => (
                <span
                  key={i}
                  className="flex items-center gap-2 text-sm font-semibold text-text-secondary"
                >
                  <i className={`${t.icon} text-primary/70`} />
                  {t.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Restart;
